import React from "react";
import { Box } from "@mui/material";
import { QuestionCard } from "../shared/QuestionCard";
import { StyledChoicePill } from "../form/StyledChoicePill";
import { Allergy, AllergyMeta } from "@/helpers/enums";
import { QUIZ } from "@/theme/quizTokens";

const NONE = "NONE_OPTION";

type Props = {
  dogName: string;
  value: string[];
  onChange: (allergies: string[]) => void;
};

export function AllergiesQuestion({ dogName, value, onChange }: Props) {
  const hasNoneSelected = value.includes(NONE);
  const hasAllergiesSelected = value.some((a) => a !== NONE);

  const toggleAllergy = (item: Allergy | typeof NONE) => {
    if (item === NONE) {
      // Toggle "None" - clears all allergies and selects None
      if (hasNoneSelected) {
        onChange([]); // Deselect None
      } else {
        onChange([NONE]); // Select None, clear allergies
      }
      return;
    }

    // Selecting an allergy - remove NONE if present
    const withoutNone = value.filter((a) => a !== NONE);
    const updated = withoutNone.includes(item)
      ? withoutNone.filter((a) => a !== item)
      : [...withoutNone, item];

    onChange(updated);
  };

  return (
    <QuestionCard
      title={`Does ${dogName || "your dog"} have any allergies?`}
      subtitle="Select all that apply"
      subtitleAbove
      field="allergies"
    >
      {/* "Nothing" option - separated */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: "18px",
        }}
      >
        <StyledChoicePill
          selected={hasNoneSelected}
          label="No allergies"
          big
          onClick={() => toggleAllergy(NONE)}
        />
      </Box>

      {/* Actual allergies */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 1.25 },
          justifyContent: "center",
          alignItems: "stretch",
        }}
      >
        {Object.values(Allergy).map((a) => (
          <StyledChoicePill
            key={a}
            selected={value.includes(a)}
            label={AllergyMeta[a].label}
            onClick={() => toggleAllergy(a)}
          />
        ))}
      </Box>
    </QuestionCard>
  );
}
